import React from 'react'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../../../state/mint/hooks'
import { TokenAmount, WETH9 } from '@uniswap/sdk-core'
import { Field } from '../../../../state/mint/actions'
import { maxAmountSpend } from '../../../../utils/maxAmountSpend'
import { useActiveWeb3React } from '../../../../hooks'
import { useWalletModalToggle } from '../../../../state/application/hooks'
import { ButtonConfirmed, ButtonLight, ButtonPrimary } from '../../../../components/Button'
import { RowBetween } from '../../../../components/Row'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { formatUnits, parseUnits } from 'ethers/lib/utils'
import { useTokenBalance } from '../../../../state/wallet/hooks'
import { calculateGasMargin, getContract } from '../../../../utils'
import { Text } from 'rebass'
import {
  CURRENT_CHAIN,
  LHYPE_ADDRESS,
  LHYPE_ATOMIC_QUEUE_ADDRESS,
  LHYPE_ACCOUNTANT_ADDRESS,
  STHYPE_ADDRESS,
} from '@hyperswap/addresses'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import styled from 'styled-components'
import { useToken } from 'hooks/Tokens'
import { abi as LHYPE_ATOMIC_QUEUE } from '../../../../abis/looped/AtomicQueueUCP.json'
import { abi as LHYPE_ACCOUNTANT_ABI } from '../../../../abis/looped/AccountantWithRateProviders.json'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { Dots } from 'pages/Pool/styleds'
import { useUSDCValue } from 'hooks/useUSDCPrice'

interface UnstakeProps {
  key?: number
  setRefresh: () => void
}

export const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const Unstake: React.FC<UnstakeProps> = ({ setRefresh }) => {
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { account, chainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const token = useToken(LHYPE_ADDRESS)
  const tokenBalance = useTokenBalance(account ?? undefined, token ?? undefined)

  // allowance handling
  const [signatureData] = React.useState<{
    v: number
    r: string
    s: string
    deadline: number
  } | null>(null)

  const [loadingRemove, setLoadingRemove] = React.useState<boolean>(false)
  const {
    dependentField,
    currencies,

    currencyBalances,
    parsedAmounts,

    noLiquidity,
  } = useDerivedMintInfo(token ?? undefined, undefined)

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)
  const fiatValueInput = useUSDCValue(parsedAmounts[Field.CURRENCY_A])

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )

  const [approval, approveCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], LHYPE_ATOMIC_QUEUE_ADDRESS)

  async function onAttemptToApprove() {
    const liquidityAmount = parsedAmounts[Field.CURRENCY_A]

    if (!liquidityAmount) throw new Error('missing liquidity amount')
    approveCallback()
  }

  const addTransaction = useTransactionAdder()

  async function onWithdraw() {
    if (!chainId || !library || !account) return
    const atomicQueueContract = getContract(LHYPE_ATOMIC_QUEUE_ADDRESS, LHYPE_ATOMIC_QUEUE, library, account)
    const accountantContract = getContract(LHYPE_ACCOUNTANT_ADDRESS, LHYPE_ACCOUNTANT_ABI, library, account)
    const rateRaw = await accountantContract.getRateInQuote(WETH9[CURRENT_CHAIN].address)
    const rate = formatUnits(rateRaw, 18)
    const slippageTolerance = 0.005

    const atomicPrice = parseFloat(rate) * (1 - slippageTolerance)

    const deadline = Math.floor(Date.now() / 1000) + 3 * 24 * 60 * 60

    const estimate = atomicQueueContract.estimateGas.updateAtomicRequest
    const method = atomicQueueContract.updateAtomicRequest
    const args = [
      LHYPE_ADDRESS,
      STHYPE_ADDRESS,
      {
        deadline: deadline,
        atomicPrice: parseUnits(`${atomicPrice}`, 18).div(1000).mul(995),
        offerAmount: parseUnits(`${parsedAmounts[Field.CURRENCY_A]?.toExact()}`, 18).toString(),
        inSolve: false,
      },
    ]

    setLoadingRemove(true)

    await estimate(...args, {})
      .then((estimatedGasLimit) =>
        method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Unstake HYPE`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          await wait(4000)
          setRefresh()
          setLoadingRemove(false)
        })
      )
      .catch((error) => {
        setLoadingRemove(false)
        console.error(error)
      })
  }

  const isDisabled =
    loadingRemove === true ||
    formattedAmounts[Field.CURRENCY_A].length === 0 ||
    parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
    (tokenBalance && parseFloat(formattedAmounts[Field.CURRENCY_A]) > parseFloat(tokenBalance?.toExact())) ||
    approval !== ApprovalState.APPROVED

  return (
    <ContainerStyled>
      <CurrencyInputPanel
        value={formattedAmounts[Field.CURRENCY_A]}
        onUserInput={onFieldAInput}
        onMax={() => {
          onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
        }}
        showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
        currency={currencies[Field.CURRENCY_A]}
        id="unstake-lhype"
        showCommonBases
        smallTokenName
        fiatValue={fiatValueInput}
      />

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
        ) : (
          <RowBetween>
            <ButtonConfirmed
              onClick={onAttemptToApprove}
              confirmed={approval === ApprovalState.APPROVED || signatureData !== null}
              disabled={approval !== ApprovalState.NOT_APPROVED || signatureData !== null}
              mr="0.5rem"
              fontWeight={400}
              fontSize={12}
            >
              {approval === ApprovalState.PENDING ? (
                <Dots>Approving</Dots>
              ) : approval === ApprovalState.APPROVED || signatureData !== null ? (
                'Approved'
              ) : (
                'Approve'
              )}
            </ButtonConfirmed>
            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onWithdraw()}
              disabled={isDisabled}
            >
              <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {loadingRemove ? 'Unstaking...' : 'Unstake'}
              </Text>
            </ButtonPrimary>
          </RowBetween>
        )}
      </div>
    </ContainerStyled>
  )
}
export default Unstake
